import { useEffect, useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAgentStore } from '../store/agentStore';
import { useNotificationStore } from '../store/notificationStore';
import {
  connectSocket,
  disconnectSocket,
  getSocket,
  isSocketConnected,
  type NotificationPayload,
  type WalletBalanceUpdatedEvent,
  type TransactionReceivedEvent,
  type TransactionSentEvent,
} from '../services/socket';
import type { AppNotification } from '../types/notification.types';

// ─── Module-level dedup guard ─────────────────────────────────────────────────
// Single source of truth: even if React mounts the hook twice (StrictMode) or
// the socket emits the same event twice, each unique ID is only processed once.
const _seenEventIds = new Set<string>();
function _once(id: string | undefined): boolean {
  if (!id) return false;              // no id → can't dedup, allow through
  if (_seenEventIds.has(id)) return true;  // duplicate → skip
  _seenEventIds.add(id);
  setTimeout(() => _seenEventIds.delete(id), 5000);
  return false;
}

// ─── Main agent notification handler — mount ONCE at app root ─────────────────

export function useAgentNotifications() {
  const qc = useQueryClient();
  const updateFloatBalance = useAgentStore((s) => s.updateFloatBalance);
  const addNotification    = useNotificationStore((s) => s.addNotification);
  const isAuthenticated    = useAgentStore((s) => s.isAuthenticated);
  const accessToken        = useAgentStore((s) => s.accessToken);

  // ── Refs so handlers never need to be recreated (stable identity forever) ──
  const balanceRef = useRef(updateFloatBalance);
  const addRef     = useRef(addNotification);
  const qcRef      = useRef(qc);
  useEffect(() => { balanceRef.current = updateFloatBalance; }, [updateFloatBalance]);
  useEffect(() => { addRef.current = addNotification; },       [addNotification]);
  useEffect(() => { qcRef.current = qc; },                     [qc]);

  // ── Stable handlers — empty deps array, identity NEVER changes ────────────
  const handleBalanceUpdated = useCallback((data: WalletBalanceUpdatedEvent) => {
    balanceRef.current(data.availableBalance);
    qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
  }, []);

  const handleTxReceived = useCallback((data: TransactionReceivedEvent) => {
    const id = `tx-in-${data.transactionId ?? data.reference}`;
    if (_once(id)) return;
    toast.success(`💵 Cash-in confirmado: ${data.amount.toLocaleString()} ${data.currency}`, { duration: 5000, id });
    qcRef.current.refetchQueries({ type: 'active', queryKey: ['transactions'] });
    qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
  }, []);

  const handleTxSent = useCallback((data: TransactionSentEvent) => {
    const id = `tx-out-${data.transactionId ?? data.reference}`;
    if (_once(id)) return;
    toast.success(`📤 Operación confirmada: ${data.amount.toLocaleString()} ${data.currency}`, { duration: 4000, id });
    qcRef.current.refetchQueries({ type: 'active', queryKey: ['transactions'] });
  }, []);

  const handleNotification = useCallback((payload: NotificationPayload) => {
    if (_once(payload.notificationId)) return;
    addRef.current(payload as Omit<AppNotification, 'read'>);
    const data = payload.data as Record<string, unknown> | undefined;
    const tid  = payload.notificationId;

    switch (payload.type) {
      case 'AGENT_APPROVED':
        toast.success(payload.message, { id: tid, duration: 8000, icon: '✅' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'AGENT_REJECTED':
        toast.error(payload.message, { id: tid, duration: 10000, icon: '❌' });
        break;
      case 'AGENT_SUSPENDED':
        toast.error(payload.message, { id: tid, duration: 10000, icon: '⛔' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'AGENT_ACTIVATED':
        toast.success(payload.message, { id: tid, duration: 8000, icon: '🔓' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'COMMISSION_EARNED': {
        const commission = data?.commission as number | undefined;
        const currency   = data?.currency   as string | undefined;
        toast.success(
          commission ? `💸 Ganaste ${commission.toLocaleString()} ${currency ?? 'XAF'}` : payload.message,
          { id: tid, duration: 5000 }
        );
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['commissions'] });
        break;
      }
      case 'COMMISSIONS_SETTLED':
        toast.success(payload.message, { id: tid, duration: 6000, icon: '💰' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['commissions'] });
        break;
      case 'FLOAT_LOW':
        toast(payload.message, { id: tid, duration: 7000, icon: '⚠️' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentFloat'] });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'FLOAT_CRITICAL':
        toast.error(payload.message, { id: tid, duration: 9000, icon: '🚨' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentFloat'] });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'FLOAT_DEPLETED':
        toast.error(payload.message, { id: tid, duration: 0, icon: '❌' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentFloat'] });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      case 'FLOAT_REPLENISHED':
      case 'float:recharge_approved': {
        const newBalance = data?.newBalance as number | undefined;
        if (newBalance !== undefined) balanceRef.current(newBalance);
        toast.success(payload.message, { id: tid, duration: 6000, icon: '✅' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['float-recharge-requests'] });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentFloat'] });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['agentMeProfile'] });
        break;
      }
      case 'FLOAT_RECHARGE_REJECTED':
      case 'float:recharge_rejected':
        toast.error(payload.message, { id: tid, duration: 8000, icon: '❌' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['float-recharge-requests'] });
        break;
      case 'DAILY_REPORT_GENERATED':
        toast(payload.message, { id: tid, duration: 5000, icon: '📊' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['dailyReports'] });
        break;
      case 'CASH_IN_COMPLETED':
      case 'CASH_OUT_INITIATED':
      case 'CASH_OUT_COMPLETED':
        toast.success(payload.message, { id: tid, duration: 4000, icon: '💵' });
        qcRef.current.refetchQueries({ type: 'active', queryKey: ['transactions'] });
        break;
      case 'SYSTEM_MAINTENANCE':
      case 'SYSTEM_UPDATE':
        toast(payload.message, { id: tid, duration: 8000, icon: 'ℹ️' });
        break;
      default:
        break;
    }
  }, []);

  // ── Socket lifecycle + listener registration ──────────────────────────────
  // This single effect owns everything: connect on login/reload, register
  // listeners, clean up on logout. No dependency on handler functions
  // (stable refs guarantee they're always current).
  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      // Logged out — ensure socket is gone
      disconnectSocket();
      return;
    }

    // Get existing socket (fresh login) or create one (page reload)
    const sock = connectSocket(accessToken);

    const register = () => {
      // off-then-on guarantees exactly one copy of each listener
      sock.off('wallet:balance_updated', handleBalanceUpdated);
      sock.off('transaction:received',   handleTxReceived);
      sock.off('transaction:sent',       handleTxSent);
      sock.off('notification:new',       handleNotification);

      sock.on('wallet:balance_updated', handleBalanceUpdated);
      sock.on('transaction:received',   handleTxReceived);
      sock.on('transaction:sent',       handleTxSent);
      sock.on('notification:new',       handleNotification);
    };

    if (sock.connected) {
      register();
    } else {
      sock.once('connect', register);
    }

    return () => {
      // Cleanup: remove listeners only (do NOT disconnect — socket outlives React)
      sock.off('connect',               register);
      sock.off('wallet:balance_updated', handleBalanceUpdated);
      sock.off('transaction:received',   handleTxReceived);
      sock.off('transaction:sent',       handleTxSent);
      sock.off('notification:new',       handleNotification);
    };
  // Only re-run when auth state changes (login / logout / token rotation)
  // Handler identities are stable (useCallback + empty deps) so they're safe to include
  }, [isAuthenticated, accessToken, handleBalanceUpdated, handleTxReceived, handleTxSent, handleNotification]);
}

// ─── Socket status hook ───────────────────────────────────────────────────────

export function useSocketStatus() {
  const [connected, setConnected] = useState(isSocketConnected);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const check = () => setConnected(isSocketConnected());
    socket.on('connect', check);
    socket.on('disconnect', check);
    return () => {
      socket.off('connect', check);
      socket.off('disconnect', check);
    };
  }, []);

  return { connected };
}

// ─── Backwards compat stubs ───────────────────────────────────────────────────

export function useSocketConnection() { /* no-op: lifecycle now in useAgentNotifications */ }
export function useFloatRechargeSocket(_onUpdate?: () => void) { /* no-op */ }
